/**
 * VoiceController: everything between the /ws/client messages and LiveKit.
 *
 * - epoch: messages with epoch < last `room.assign` epoch are ignored (§6.2);
 * - make-before-break on `room.assign` (§4.5): new Room -> connect -> publish (cloned mic track)
 *   -> `room.joined` -> disconnect the old Room;
 * - `room.leave`: disconnect from LiveKit, stop the mic, stay on the WS (§8.7);
 * - token: LiveKit refreshes it while connected; for (re)connections with an expired token the
 *   client sends `token.request` and waits for `token.issued` (§7.3, §8.11);
 * - the client NEVER subscribes by itself (R1-B): it connects with autoSubscribe=false and never
 *   calls setSubscribed();
 * - subscription safety (canSubscribe=true, see protocol/README.md):
 *   - `permissions {epoch, allow}` -> localParticipant.setTrackSubscriptionPermissions(false, allow...),
 *     applied right after connecting and BEFORE publishing (deny-all until the first message);
 *   - `gateUnassignedTracks` (default true) pauses with setEnabled(false) every subscribed track
 *     whose identity is not in the last `peers` list.
 *
 * Anti-eco / anti-Larsen (vedi audioOutput.ts, SpatialMixer.ts, howlDetector.ts):
 *   - le voci escono da un elemento <audio> (MediaStreamAudioDestinationNode), non da
 *     `ctx.destination`, così l'AEC del browser ha il riferimento giusto;
 *   - ducking: mentre trasmetti le voci in arrivo si abbassano (sempre in modalità altoparlanti,
 *     opzionale in cuffia);
 *   - rilevatore di fischio sul microfono: se scatta, il microfono viene spento subito;
 *   - modalità altoparlanti: AGC del microfono spento, soglia voce minima più alta, ducking attivo,
 *     rilevatore più sensibile.
 *
 * Tipo di collegamento di rete (vedi netStats.ts): con la Room attiva connessa si leggono le statistiche
 * WebRTC (coppia ICE selezionata) subito, dopo riconnessioni e cambi di stanza, poi ogni ~10 s; il risultato
 * (`direct`/`relay`) va al centrale con `net {epoch, net, via}` solo quando cambia o cambia l'epoch.
 *
 * Stato del microfono per il gioco (`mic {muted, deafened}`, vedi micReport()): inviato dopo ogni `hello` e poi
 * solo quando cambia, al massimo uno ogni MIC_MIN_INTERVAL_MS (l'ultimo stato vince). `muted` = Muto premuto, mute
 * automatico per fischio, microfono negato/assente/occupato/in errore; il push-to-talk a tasto rilasciato non conta
 * (il giocatore è pronto a parlare). `deafened` = ascolto disattivato. Serve ai placeholder del plugin Spigot.
 *
 * Riduzione rumore avanzata (RNNoise, vedi noiseSuppressor.ts): getUserMedia -> RNNoise -> traccia
 * pubblicata. Indicatore di livello e soglia voce leggono il segnale filtrato (quello che gli altri
 * sentono: ventole e tastiera non aprono la soglia); il rilevatore di fischio legge il microfono
 * PRIMA di RNNoise, perché RNNoise attenua i toni stazionari e nasconderebbe il fischio.
 */
import { backgroundTimeout, startBackgroundTicker, type Ticker, type TimeoutHandle } from '../spatial/backgroundTicker';
import {
  AudioPresets,
  ConnectionState,
  DisconnectReason,
  LocalAudioTrack,
  Room,
  RoomEvent,
  Track,
  type LocalTrackPublication,
  type Participant,
  type RemoteParticipant,
  type RemoteTrack,
  type RemoteTrackPublication,
} from 'livekit-client';
import {
  DEFAULT_GAIN,
  DEFAULT_INTERP_DELAY_MS,
  MIC_MIN_INTERVAL_MS,
  type AudioMode,
  type ClientToServer,
  type MatchRole,
  type MicMessage,
  type MicMode,
  type PeerInfo,
  type PermissionsMessage,
  type RoomAssignMessage,
  type RoomLeaveReason,
  type Scope,
  type ServerToClient,
  type GainConfig,
} from '../api/protocol';
import type { SocketStatus } from '../api/ws';
import { DEFAULT_DUCK_AMOUNT, SpatialMixer, type PeerAudioState } from '../spatial/SpatialMixer';
import { AudioOutput, createHiddenAudioElement, type OutputElementLike } from './audioOutput';
import { HOWL_HEADPHONES, HOWL_SPEAKERS, HowlDetector } from './howlDetector';
import { MicNoiseSuppressor, rnnoiseBackend, type NoiseSuppressionStatus, type NoiseSuppressorBackend } from './noiseSuppressor';
import { collectNetPath, netStatsSources, type EngineLike, type NetPath } from './netStats';

// ------------------------------------------------------------------ public state

export type VoicePhase =
  /** The user has not clicked "Entra" yet. */
  | 'idle'
  /** Entered, but there is no room assignment (offline in Minecraft, no scope...). */
  | 'waiting'
  | 'connecting'
  | 'connected'
  /** Connected to the old room while the new one connects (make-before-break). */
  | 'switching'
  | 'reconnecting'
  | 'failed';

export type MicStatus = 'off' | 'starting' | 'ok' | 'denied' | 'notfound' | 'busy' | 'insecure' | 'error';

export interface PeerView extends PeerInfo {
  speaking: boolean;
}

export interface VoiceSnapshot {
  socket: SocketStatus;
  me: { uuid: string; name: string } | null;
  mc: 'online' | 'offline' | 'unknown';
  server: string | null;
  scope: Scope | null;
  role: MatchRole | null;
  teamId: string | null;
  entered: boolean;
  phase: VoicePhase;
  room: string | null;
  epoch: number;
  audioMode: AudioMode;
  leaveReason: RoomLeaveReason | null;
  hasAssignment: boolean;
  mic: {
    status: MicStatus;
    muted: boolean;
    deafened: boolean;
    mode: MicMode;
    pttPressed: boolean;
    /** True when the mic is actually sent to LiveKit (not muted, PTT held...). */
    transmitting: boolean;
    /** Automatic voice activation (true) or manual threshold gate (false). */
    autoSensitivity: boolean;
    /** Manual gate threshold on the 0..1 level scale. */
    threshold: number;
    /** Manual gate currently open (always true with automatic sensitivity in headphone mode). */
    gateOpen: boolean;
    /** Soglia effettiva del gate (null = sempre aperto: automatica in cuffia). */
    effectiveThreshold: number | null;
  };
  /** Modalità altoparlanti (casse invece delle cuffie). */
  speakerMode: boolean;
  ducking: {
    /** Ducking attivo per le impostazioni correnti (sempre in modalità altoparlanti). */
    enabled: boolean;
    /** Riduzione delle voci in arrivo mentre parli, 0..1. */
    amount: number;
    /** In questo momento le voci sono abbassate. */
    active: boolean;
  };
  /** Rilevatore di fischio: `detected` resta true finché l'utente non riattiva il microfono. */
  howl: { detected: boolean; frequency: number | null };
  /** Riduzione rumore avanzata (RNNoise). */
  noiseSuppression: { enabled: boolean; status: NoiseSuppressionStatus; error: string | null };
  peers: PeerView[];
  /** Tipo di collegamento della mia connessione LiveKit attiva (null = sconosciuto / non connesso). */
  selfNet: NetPath | null;
  selfSpeaking: boolean;
  audioBlocked: boolean;
  lastError: string | null;
  gain: GainConfig;
}

// ------------------------------------------------------------------ dependencies (mockable)

export interface RoomLike {
  readonly name: string;
  readonly state: ConnectionState | string;
  readonly localParticipant: {
    publishTrack(track: LocalAudioTrack, opts?: object): Promise<LocalTrackPublication>;
    setTrackSubscriptionPermissions(allParticipantsAllowed: boolean, permissions?: Array<{ participantIdentity?: string; allowAll?: boolean }>): void;
    readonly identity: string;
  };
  readonly remoteParticipants: Map<string, RemoteParticipant>;
  connect(url: string, token: string, opts?: object): Promise<void>;
  disconnect(stopTracks?: boolean): Promise<void>;
  on(event: string, listener: (...args: any[]) => void): unknown;
  switchActiveDevice?(kind: MediaDeviceKind, deviceId: string): Promise<boolean>;
  /** livekit-client: RTCEngine con `pcManager` (publisher/subscriber), usato solo per le statistiche di rete. */
  readonly engine?: EngineLike;
}

export interface LocalMicTrack {
  readonly mediaStreamTrack: MediaStreamTrack;
  mute(): Promise<unknown>;
  unmute(): Promise<unknown>;
  replaceTrack(track: MediaStreamTrack, userProvided?: boolean): Promise<unknown>;
  stop(): void;
  /** API pubblica di LocalTrack (= RTCRtpSender.getStats()). */
  getRTCStatsReport?(): Promise<RTCStatsReport | undefined>;
}

export interface VoiceDeps {
  createAudioContext(): AudioContext;
  createRoom(ctx: AudioContext): RoomLike;
  getUserMedia(constraints: MediaStreamConstraints): Promise<MediaStream>;
  createLocalTrack(mst: MediaStreamTrack, ctx: AudioContext): LocalMicTrack;
  createMixer(ctx: AudioContext): SpatialMixer;
  /** Elemento <audio> nascosto che riproduce il mix delle voci (null = ripiego su ctx.destination). */
  createOutputElement(): OutputElementLike | null;
  /** RNNoise (AudioWorklet + WASM). */
  noiseSuppressor: NoiseSuppressorBackend;
  now(): number;
  setTimeout(fn: () => void, ms: number): unknown;
  clearTimeout(id: unknown): void;
  /** Timer periodico della misura di rete. */
  setInterval(fn: () => void, ms: number): unknown;
  clearInterval(id: unknown): void;
  isSecureContext(): boolean;
}

export const defaultVoiceDeps: VoiceDeps = {
  createAudioContext: () => new AudioContext({ latencyHint: 'interactive' }),
  createRoom: (ctx) =>
    new Room({
      adaptiveStream: false,
      dynacast: false,
      webAudioMix: { audioContext: ctx },
      disconnectOnPageLeave: true,
    }) as unknown as RoomLike,
  getUserMedia: (c) => navigator.mediaDevices.getUserMedia(c),
  createLocalTrack: (mst, ctx) => new LocalAudioTrack(mst, undefined, true, ctx) as unknown as LocalMicTrack,
  createMixer: (ctx) => new SpatialMixer(ctx),
  createOutputElement: () => createHiddenAudioElement(),
  noiseSuppressor: rnnoiseBackend,
  now: () => Date.now(),
  // Timer nel worker: non rallentati da Chrome con la finestra ridotta a icona (backgroundTicker.ts).
  setTimeout: (fn, ms) => backgroundTimeout(ms, fn),
  clearTimeout: (id) => (id as TimeoutHandle | null)?.cancel(),
  setInterval: (fn, ms) => startBackgroundTicker(ms, fn),
  clearInterval: (id) => (id as Ticker | null)?.stop(),
  isSecureContext: () => typeof window === 'undefined' || window.isSecureContext !== false,
};

export interface VoiceControllerOptions {
  /** Pause tracks of identities not in the `peers` list. Default true. */
  gateUnassignedTracks?: boolean;
  micMode?: MicMode;
  autoSensitivity?: boolean;
  vadThreshold?: number;
  startMuted?: boolean;
  inputDeviceId?: string | null;
  outputDeviceId?: string | null;
  masterVolume?: number;
  /** Modalità altoparlanti. Default false. */
  speakerMode?: boolean;
  /** Ducking anche in modalità cuffie. Default false. */
  duckInHeadphones?: boolean;
  /** Riduzione delle voci durante il ducking, 0..1. Default 0.5. */
  duckAmount?: number;
  /** Riduzione rumore avanzata (RNNoise). Default false. */
  advancedNoiseSuppression?: boolean;
  /** Audio mono (nessuna direzione). Default false (stereo con HRTF). */
  mono?: boolean;
  /** Effetto prossimità (volume che cala con la distanza). Default true. */
  proximity?: boolean;
  peerPref?: (identity: string) => { volume: number; muted: boolean };
}

/** Token considered expired this long before `expiresAt`. */
const TOKEN_SAFETY_MS = 5_000;
const MAX_RECONNECT_TRIES = 5;
const SELF_SPEAKING_LEVEL = 0.14;
/** The manual gate stays open this long after the level drops below the threshold. */
const GATE_HOLD_MS = 300;
const LEVEL_TICK_MS = 40;
/** Modalità altoparlanti: soglia minima del gate manuale (scala 0..1 dell'indicatore). */
export const SPEAKER_MIN_THRESHOLD = 0.15;
/** Modalità altoparlanti con sensibilità automatica: gate minimo che taglia l'eco residuo. */
export const SPEAKER_AUTO_GATE = 0.08;
/** Analyser dedicato al rilevatore di fischio (≈23 Hz per bin a 48 kHz). */
const HOWL_FFT_SIZE = 2048;
/** Misura del collegamento di rete: ogni 10 s a regime... */
export const NET_PROBE_MS = 10_000;
/** ...e ogni 2 s finché per l'epoch corrente non si è riusciti a inviarla (ICE appena connesso). */
export const NET_TICK_MS = 2_000;

interface ManagedRoom {
  room: RoomLike;
  name: string;
  epoch: number;
  mic: LocalMicTrack | null;
  publication: LocalTrackPublication | null;
  /** Set when we disconnect it on purpose. */
  closing: boolean;
  /** Remote audio tracks attached to the mixer, by track sid. */
  tracks: Map<string, { identity: string; key: string; track: RemoteTrack; publication: RemoteTrackPublication; element: HTMLMediaElement | null }>;
}

type Listener = () => void;

export class VoiceController {
  private readonly deps: VoiceDeps;
  private readonly opts: VoiceControllerOptions;
  private send: (msg: ClientToServer) => boolean = () => false;
  private listeners = new Set<Listener>();
  private snap: VoiceSnapshot;

  private ctx: AudioContext | null = null;
  private mixer: SpatialMixer | null = null;
  /** Microfono grezzo (dopo l'AEC del browser, prima di RNNoise). */
  private source: MediaStream | null = null;
  /** Stream trasmesso: filtrato da RNNoise se attivo, altrimenti `source`. */
  private tx: MediaStream | null = null;
  private ns: MicNoiseSuppressor | null = null;
  private nsEnabled: boolean;
  private levelSourceNode: MediaStreamAudioSourceNode | null = null;
  /** Riaperture del microfono in fila (toggle rapidi). */
  private micQueue: Promise<void> = Promise.resolve();
  private micAnalyser: AnalyserNode | null = null;
  private micSourceNode: MediaStreamAudioSourceNode | null = null;
  private micBuf: Float32Array<ArrayBuffer> | null = null;
  private output: AudioOutput | null = null;
  private howlAnalyser: AnalyserNode | null = null;
  private howlBuf: Float32Array<ArrayBuffer> | null = null;
  private howlDetector: HowlDetector | null = null;
  private speakerMode: boolean;
  private duckInHeadphones: boolean;
  private duckAmount: number;
  private mono: boolean;
  private proximity: boolean;
  /** Fino a quando il livello conta ancora come voce (ducking con sensibilità automatica). */
  private voiceHoldUntil = 0;
  private lastGateNow = 0;

  private epoch = 0;
  private assignment: RoomAssignMessage | null = null;
  private active: ManagedRoom | null = null;
  private pending: ManagedRoom | null = null;
  private awaitingToken = false;
  private reconnectTries = 0;
  private reconnectTimer: unknown = null;
  private peerInfo = new Map<string, PeerInfo>();
  private permissions: PermissionsMessage | null = null;
  private speaking = new Set<string>();
  private gainCfg: GainConfig = DEFAULT_GAIN;
  private interpDelayMs = DEFAULT_INTERP_DELAY_MS;
  private outputDeviceId: string | null;
  private inputDeviceId: string | null;
  private masterVolume: number;
  private disposed = false;
  private level = 0;
  private gateHoldUntil = 0;
  /** campionamento del microfono (soglia voce, ducking, fischio): ticker nel worker, non rallentato in background */
  private levelTimer: Ticker | null = null;
  /** Misura di rete: timer, ultima `net` inviata (con la sua epoch), misura in corso, ultima misura. */
  private netTimer: unknown = null;
  private lastNet: (NetPath & { epoch: number }) | null = null;
  private netBusy = false;
  private netProbedAt = 0;
  /**
   * Stato `mic` per il centrale: `micReady` dopo `hello` (prima il WS non è pronto), ultimo inviato con successo
   * (null = da inviare), istante dell'ultimo invio e timer dell'invio ritardato (limite di frequenza).
   */
  private micReady = false;
  private lastMic: MicMessage | null = null;
  private micSentAt = 0;
  private micTimer: unknown = null;

  constructor(deps: Partial<VoiceDeps> = {}, opts: VoiceControllerOptions = {}) {
    this.deps = { ...defaultVoiceDeps, ...deps };
    this.opts = opts;
    this.outputDeviceId = opts.outputDeviceId ?? null;
    this.inputDeviceId = opts.inputDeviceId ?? null;
    this.masterVolume = opts.masterVolume ?? 1;
    this.speakerMode = opts.speakerMode ?? false;
    this.duckInHeadphones = opts.duckInHeadphones ?? false;
    this.duckAmount = clamp01(opts.duckAmount ?? DEFAULT_DUCK_AMOUNT);
    this.nsEnabled = opts.advancedNoiseSuppression ?? false;
    this.mono = opts.mono ?? false;
    this.proximity = opts.proximity ?? true;
    const autoSensitivity = opts.autoSensitivity ?? true;
    const threshold = opts.vadThreshold ?? 0.12;
    this.snap = {
      socket: 'idle',
      me: null,
      mc: 'unknown',
      server: null,
      scope: null,
      role: null,
      teamId: null,
      entered: false,
      phase: 'idle',
      room: null,
      epoch: 0,
      audioMode: 'spatial',
      leaveReason: null,
      hasAssignment: false,
      mic: {
        status: 'off',
        muted: opts.startMuted ?? false,
        deafened: false,
        mode: opts.micMode ?? 'vad',
        pttPressed: false,
        transmitting: false,
        autoSensitivity,
        threshold,
        gateOpen: autoSensitivity && !this.speakerMode,
        effectiveThreshold: effectiveThreshold(autoSensitivity, threshold, this.speakerMode),
      },
      speakerMode: this.speakerMode,
      ducking: { enabled: this.speakerMode || this.duckInHeadphones, amount: this.duckAmount, active: false },
      howl: { detected: false, frequency: null },
      noiseSuppression: {
        enabled: this.nsEnabled,
        status: this.deps.noiseSuppressor.isSupported() ? 'off' : 'unsupported',
        error: null,
      },
      peers: [],
      selfNet: null,
      selfSpeaking: false,
      audioBlocked: false,
      lastError: null,
      gain: DEFAULT_GAIN,
    };
  }

  // ------------------------------------------------------------------ store API (useSyncExternalStore)

  subscribe = (l: Listener): (() => void) => {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  };

  getSnapshot = (): VoiceSnapshot => this.snap;

  private update(patch: Partial<VoiceSnapshot>): void {
    this.snap = { ...this.snap, ...patch };
    if (patch.mic || patch.howl) this.syncMic();
    for (const l of this.listeners) l();
  }

  private updateMic(patch: Partial<VoiceSnapshot['mic']>): void {
    const mic = { ...this.snap.mic, ...patch };
    mic.transmitting = this.computeTransmit(mic);
    mic.effectiveThreshold = effectiveThreshold(mic.autoSensitivity, mic.threshold, this.speakerMode);
    this.update({ mic });
    this.refreshDucking();
  }

  // ------------------------------------------------------------------ wiring

  /** Function used to send messages on /ws/client. */
  setSender(send: (msg: ClientToServer) => boolean): void {
    this.send = send;
  }

  setSocketStatus(socket: SocketStatus): void {
    // WS non più aperto: il centrale dimentica `mic`, si rimanda tutto al prossimo `hello`
    if (socket !== 'open') this.resetMicSync();
    this.update({ socket });
  }

  /** Current epoch (last room.assign / room.leave). */
  getEpoch(): number {
    return this.epoch;
  }

  getMixer(): SpatialMixer | null {
    return this.mixer;
  }

  getActiveRoom(): RoomLike | null {
    return this.active?.room ?? null;
  }

  // ------------------------------------------------------------------ incoming messages

  handleMessage(msg: ServerToClient): void {
    if (this.disposed) return;
    switch (msg.type) {
      case 'hello':
        this.gainCfg = msg.config.gain;
        this.interpDelayMs = msg.config.interpDelayMs;
        this.mixer?.configure(this.gainCfg, this.interpDelayMs);
        this.update({ me: { uuid: msg.uuid, name: msg.name }, gain: msg.config.gain });
        // connessione (o riconnessione) nuova: il centrale non conosce lo stato del microfono
        this.resetMicSync();
        this.micReady = true;
        this.syncMic();
        break;
      case 'state':
        this.update({
          mc: msg.mc,
          server: msg.server,
          scope: msg.scope,
          role: msg.role ?? null,
          teamId: msg.teamId ?? null,
        });
        break;
      case 'room.assign':
        this.onRoomAssign(msg);
        break;
      case 'room.leave':
        if (msg.epoch < this.epoch) return;
        this.epoch = msg.epoch;
        this.assignment = null;
        this.awaitingToken = false;
        this.cancelReconnect();
        void this.closeAll();
        this.stopMic();
        this.peerInfo.clear();
        this.permissions = null;
        this.speaking.clear();
        this.mixer?.resetSpatial();
        this.update({
          epoch: this.epoch,
          leaveReason: msg.reason,
          hasAssignment: false,
          room: null,
          peers: [],
          phase: this.snap.entered ? 'waiting' : 'idle',
        });
        break;
      case 'token.issued':
        if (msg.epoch !== this.epoch || !this.assignment) return;
        this.assignment = { ...this.assignment, token: msg.token, expiresAt: msg.expiresAt };
        if (this.awaitingToken) {
          this.awaitingToken = false;
          if (this.snap.entered) void this.join(this.assignment, true);
        }
        break;
      case 'peers':
        if (msg.epoch < this.epoch) return;
        this.peerInfo = new Map(msg.peers.map((p) => [p.identity, p]));
        for (const p of msg.peers) this.mixer?.setPeerRadio(p.identity, p.radio);
        this.applyGating();
        this.refreshPeers();
        break;
      case 'permissions':
        if (msg.epoch < this.epoch) return;
        this.permissions = msg;
        for (const m of [this.active, this.pending]) {
          if (m && m.epoch === msg.epoch && this.isConnected(m.room)) this.applyPermissions(m);
        }
        break;
      case 'spatial':
        if (msg.epoch < this.epoch) return;
        this.mixer?.pushFrame(msg);
        break;
      case 'kick':
        // The socket stops by itself; leave LiveKit too.
        this.assignment = null;
        void this.closeAll();
        this.stopMic();
        this.update({ phase: this.snap.entered ? 'waiting' : 'idle', hasAssignment: false, room: null, peers: [] });
        break;
      case 'error':
        this.update({ lastError: `${msg.code}: ${msg.message}` });
        break;
      case 'pong':
        break;
    }
  }

  private onRoomAssign(msg: RoomAssignMessage): void {
    if (msg.epoch < this.epoch) return;
    this.epoch = msg.epoch;
    this.assignment = msg;
    this.awaitingToken = false;
    this.reconnectTries = 0;
    this.cancelReconnect();
    this.mixer?.setMode(msg.audio.mode);
    this.update({
      epoch: msg.epoch,
      audioMode: msg.audio.mode,
      hasAssignment: true,
      leaveReason: null,
      scope: msg.scope,
    });
    if (this.snap.entered) void this.join(msg, false);
  }

  // ------------------------------------------------------------------ user actions

  /**
   * Click on "Entra": creates/resumes the AudioContext (autoplay policy) and opens the mic
   * (getUserMedia needs a user gesture on some browsers). Then joins the assigned room, if any.
   */
  async enter(): Promise<void> {
    if (this.snap.entered) return;
    this.holdKeepAliveLock();
    if (!this.ctx) {
      this.ctx = this.deps.createAudioContext();
      this.ctx.onstatechange = () => this.update({ audioBlocked: this.isAudioBlocked() });
      this.mixer = this.deps.createMixer(this.ctx);
      this.mixer.configure(this.gainCfg, this.interpDelayMs);
      this.mixer.setMasterVolume(this.masterVolume);
      this.mixer.setMono(this.mono);
      this.mixer.setProximity(this.proximity);
      if (this.assignment) this.mixer.setMode(this.assignment.audio.mode);
      for (const p of this.peerInfo.values()) this.mixer.setPeerRadio(p.identity, p.radio);
      // Mix finale -> MediaStreamAudioDestinationNode -> <audio> (riferimento per l'AEC).
      this.output = new AudioOutput(this.ctx, this.mixer.output, () => this.deps.createOutputElement());
      this.mixer.setDucking(false, this.duckAmount);
      if (this.outputDeviceId) void this.applyOutputDevice(this.outputDeviceId);
    }
    // Autoplay: play() dell'elemento parte qui, ancora dentro il clic su "Entra" (prima di ogni
    // await: Safari lo richiede), insieme a resume() dell'AudioContext.
    const playing = this.output?.play();
    try {
      await this.ctx.resume();
    } catch {
      /* reported through audioBlocked */
    }
    await playing;
    this.mixer?.start();
    this.update({ entered: true, audioBlocked: this.isAudioBlocked(), phase: this.assignment ? 'connecting' : 'waiting' });
    await this.startMic();
    if (this.assignment && !this.active && !this.pending) await this.join(this.assignment, false);
  }

  /** Leaves the voice chat (stays linked; can enter again). */
  async exit(): Promise<void> {
    this.cancelReconnect();
    this.awaitingToken = false;
    await this.closeAll();
    this.stopMic();
    this.mixer?.stop();
    this.releaseKeepAliveLock();
    this.update({ entered: false, phase: 'idle', room: null, peers: [] });
  }

  /**
   * Web Lock tenuto finché si è in voce: Chrome non congela né scarica dalla memoria le schede
   * che possiedono un lock (Risparmio energetico / Risparmio memoria), quindi la voce continua
   * anche con la scheda nascosta o la finestra ridotta a icona. Senza Web Locks non fa nulla.
   */
  private keepAlive: AbortController | null = null;

  private holdKeepAliveLock(): void {
    if (this.keepAlive) return;
    const locks = (globalThis.navigator as (Navigator & { locks?: LockManager }) | undefined)?.locks;
    if (!locks?.request) return;
    const ctrl = new AbortController();
    this.keepAlive = ctrl;
    locks
      .request(`titanet-voice-keepalive-${Math.random().toString(36).slice(2)}`, { signal: ctrl.signal }, () =>
        new Promise<void>((resolve) => ctrl.signal.addEventListener('abort', () => resolve(), { once: true })),
      )
      .catch(() => {
        /* annullato all'uscita, o lock non disponibile */
      });
  }

  private releaseKeepAliveLock(): void {
    this.keepAlive?.abort();
    this.keepAlive = null;
  }

  /** Resume a suspended AudioContext (e.g. after an autoplay block). */
  async resumeAudio(): Promise<void> {
    if (!this.ctx) return;
    const playing = this.output?.play();
    try {
      await this.ctx.resume();
    } catch {
      /* ignore */
    }
    await playing;
    this.update({ audioBlocked: this.isAudioBlocked() });
  }

  /** Audio in pausa: AudioContext sospeso o elemento <audio> bloccato dall'autoplay. */
  private isAudioBlocked(): boolean {
    return this.ctx?.state !== 'running' || this.output?.isBlocked === true;
  }

  setMuted(muted: boolean): void {
    // Riattivare il microfono chiude anche l'avviso del fischio.
    if (!muted && this.snap.howl.detected) this.update({ howl: { detected: false, frequency: null } });
    this.howlDetector?.reset();
    this.updateMic({ muted });
    void this.applyTransmit();
  }

  /** Pulsante dell'avviso fischio: chiude l'avviso e riaccende il microfono. */
  resumeAfterHowl(): void {
    this.setMuted(false);
  }

  /**
   * Modalità altoparlanti: AGC spento (riacquisisce il microfono se è aperto), soglia voce minima
   * più alta, ducking sempre attivo, rilevatore di fischio più sensibile.
   */
  async setSpeakerMode(on: boolean): Promise<void> {
    if (on === this.speakerMode) return;
    this.speakerMode = on;
    this.howlDetector?.setConfig(on ? HOWL_SPEAKERS : HOWL_HEADPHONES);
    this.update({ speakerMode: on });
    this.updateMic({});
    this.evaluateGate(this.deps.now());
    this.applyGate();
    if (this.source) await this.reacquireMic();
  }

  /**
   * Riduzione rumore avanzata. Si applica subito, senza ricollegarsi a LiveKit: il microfono viene
   * riaperto (cambia `noiseSuppression` del browser) e la traccia pubblicata sostituita.
   */
  async setAdvancedNoiseSuppression(on: boolean): Promise<void> {
    this.nsEnabled = on;
    const ns = this.snap.noiseSuppression;
    // Dopo un errore si può riprovare riattivando il toggle.
    const status: NoiseSuppressionStatus = ns.status === 'unsupported' ? 'unsupported' : ns.status === 'error' && !on ? 'error' : 'off';
    this.update({ noiseSuppression: { enabled: on, status, error: status === 'error' ? ns.error : null } });
    if (this.source) await this.reacquireMic();
  }

  private setNsStatus(status: NoiseSuppressionStatus, error: string | null = null): void {
    const ns = this.snap.noiseSuppression;
    if (ns.status !== status || ns.error !== error || ns.enabled !== this.nsEnabled) {
      this.update({ noiseSuppression: { enabled: this.nsEnabled, status, error } });
    }
  }

  /** RNNoise va usato: richiesto e non già fallito o non supportato. */
  private nsWanted(): boolean {
    const st = this.snap.noiseSuppression.status;
    return this.nsEnabled && st !== 'unsupported' && st !== 'error';
  }

  /** Costruisce lo stream da trasmettere a partire dal microfono grezzo. */
  private async buildTx(raw: MediaStream): Promise<MediaStream> {
    if (!this.nsWanted() || !this.ctx) {
      this.ns?.stop();
      if (this.snap.noiseSuppression.status === 'on' || this.snap.noiseSuppression.status === 'loading') this.setNsStatus('off');
      return raw;
    }
    this.setNsStatus('loading');
    try {
      this.ns ??= new MicNoiseSuppressor(this.deps.noiseSuppressor, this.ctx);
      const out = await this.ns.start(raw);
      this.setNsStatus('on');
      return out;
    } catch (e) {
      this.ns?.dispose();
      this.ns = null;
      this.setNsStatus('error', e instanceof Error ? `${e.name}: ${e.message}` : String(e));
      return raw;
    }
  }

  /**
   * Apre il microfono e prepara lo stream da trasmettere. Se RNNoise non parte, il microfono era
   * stato aperto senza la soppressione del browser: lo si riapre con quella (ripiego pulito).
   */
  private async openMic(): Promise<{ raw: MediaStream; tx: MediaStream } | null> {
    const raw = await this.acquireMic();
    if (!raw) return null;
    const wanted = this.nsWanted();
    const tx = await this.buildTx(raw);
    if (wanted && tx === raw) {
      raw.getTracks().forEach((t) => t.stop());
      const again = await this.acquireMic();
      return again ? { raw: again, tx: again } : null;
    }
    return { raw, tx };
  }

  /** Audio mono (true) o stereo 3D (false): si applica subito, anche durante la voce. */
  setMono(mono: boolean): void {
    this.mono = mono;
    this.mixer?.setMono(mono);
  }

  /** Effetto prossimità on/off: si applica subito, anche durante la voce. */
  setProximity(on: boolean): void {
    this.proximity = on;
    this.mixer?.setProximity(on);
  }

  /** Ducking in modalità cuffie (in modalità altoparlanti è sempre attivo) e quanto abbassare. */
  setDuckingPrefs(inHeadphones: boolean, amount: number): void {
    this.duckInHeadphones = inHeadphones;
    this.duckAmount = clamp01(amount);
    this.refreshDucking();
  }

  /** Per il ducking: l'utente sta parlando (trasmette davvero). */
  private isTalking(now: number): boolean {
    const mic = this.snap.mic;
    if (!mic.transmitting) return false;
    if (mic.mode === 'ptt') return true; // push-to-talk premuto
    if (mic.effectiveThreshold !== null) return mic.gateOpen; // soglia voce aperta
    return now < this.voiceHoldUntil; // sensibilità automatica: livello da voce
  }

  private refreshDucking(now = this.lastGateNow): void {
    const enabled = this.speakerMode || this.duckInHeadphones;
    const active = enabled && this.isTalking(now);
    const d = this.snap.ducking;
    if (d.enabled !== enabled || d.amount !== this.duckAmount || d.active !== active) {
      this.update({ ducking: { enabled, amount: this.duckAmount, active } });
    }
    this.mixer?.setDucking(active, this.duckAmount);
  }

  // ------------------------------------------------------------------ stato del microfono per il gioco (mic)

  /**
   * Stato del microfono da mostrare in gioco. `muted`: il giocatore non trasmette per scelta (Muto, mute automatico
   * per fischio finché non lo riattiva) o perché il microfono non è disponibile (negato, assente, occupato, contesto
   * non sicuro, errore). Il push-to-talk con il tasto rilasciato NON è muto, e nemmeno `off`/`starting` (microfono
   * chiuso fuori dalla voce o in apertura: evita sfarfallii). `deafened`: ascolto disattivato (pulsante "Audio").
   */
  micReport(): MicMessage {
    const mic = this.snap.mic;
    const unavailable = mic.status === 'denied' || mic.status === 'notfound' || mic.status === 'busy' || mic.status === 'insecure' || mic.status === 'error';
    return { type: 'mic', muted: mic.muted || this.snap.howl.detected || unavailable, deafened: mic.deafened };
  }

  private resetMicSync(): void {
    this.micReady = false;
    this.lastMic = null;
    this.micSentAt = 0;
    if (this.micTimer !== null) this.deps.clearTimeout(this.micTimer);
    this.micTimer = null;
  }

  /**
   * Invia `mic` se diverso dall'ultimo inviato. Al massimo uno ogni MIC_MIN_INTERVAL_MS: dentro l'intervallo si
   * programma un invio ritardato che manda lo stato di quel momento (se nel frattempo è tornato uguale, nulla).
   */
  private syncMic(): void {
    if (!this.micReady || this.disposed || this.micTimer !== null) return;
    const cur = this.micReport();
    const last = this.lastMic;
    if (last && last.muted === cur.muted && last.deafened === cur.deafened) return;
    const wait = this.micSentAt + MIC_MIN_INTERVAL_MS - this.deps.now();
    if (this.lastMic && wait > 0) {
      this.micTimer = this.deps.setTimeout(() => {
        this.micTimer = null;
        this.syncMic();
      }, wait);
      return;
    }
    if (this.send(cur)) {
      this.lastMic = cur;
      this.micSentAt = this.deps.now();
    }
  }

  toggleMute(): void {
    // Unmuting while deafened also undeafens (Discord-like).
    if (this.snap.mic.muted && this.snap.mic.deafened) this.setDeafened(false);
    this.setMuted(!this.snap.mic.muted);
  }

  setDeafened(deafened: boolean): void {
    this.mixer?.setDeafened(deafened);
    this.updateMic({ deafened });
    void this.applyTransmit();
  }

  toggleDeafen(): void {
    this.setDeafened(!this.snap.mic.deafened);
  }

  setMicMode(mode: MicMode): void {
    this.updateMic({ mode, pttPressed: false });
    this.send({ type: 'prefs', micMode: mode });
    void this.applyTransmit();
  }

  setPushToTalk(pressed: boolean): void {
    if (this.snap.mic.pttPressed === pressed) return;
    this.updateMic({ pttPressed: pressed });
    void this.applyTransmit();
  }

  setMasterVolume(v: number): void {
    this.masterVolume = v;
    this.mixer?.setMasterVolume(v);
  }

  setPeerVolume(identity: string, volume: number): void {
    this.mixer?.setPeerVolume(identity, volume);
  }

  setPeerMuted(identity: string, muted: boolean): void {
    this.mixer?.setPeerMuted(identity, muted);
  }

  async setInputDevice(deviceId: string | null): Promise<void> {
    this.inputDeviceId = deviceId;
    if (!this.snap.entered || !this.source) return;
    await this.reacquireMic();
  }

  /** Riapre il microfono con i vincoli correnti e sostituisce la traccia pubblicata. */
  private reacquireMic(): Promise<void> {
    const run = async () => {
      if (!this.source) return;
      const old = this.source;
      const next = await this.openMic();
      if (!next) return;
      const mst = next.tx.getAudioTracks()[0]!;
      for (const r of [this.active, this.pending]) {
        if (r?.mic) await r.mic.replaceTrack(mst.clone(), true);
      }
      if (old !== next.raw) old.getTracks().forEach((t) => t.stop());
      this.source = next.raw;
      this.tx = next.tx;
      this.attachMicAnalyser();
      await this.applyTransmit();
    };
    this.micQueue = this.micQueue.then(run).catch(() => undefined);
    return this.micQueue;
  }

  async setOutputDevice(deviceId: string | null): Promise<boolean> {
    this.outputDeviceId = deviceId;
    return this.applyOutputDevice(deviceId ?? '');
  }

  /** Mic level 0..1 (RMS, scaled), for the meter. Sampled every 40 ms while the mic is open. */
  getMicLevel(): number {
    return this.level;
  }

  /**
   * Voice activation: automatic (LiveKit/Opus DTX, default) or a manual gate on the level meter.
   * The gate toggles `enabled` on the published track (silence, no renegotiation, no signalling).
   */
  setVoiceGate(autoSensitivity: boolean, threshold: number): void {
    this.updateMic({ autoSensitivity, threshold: Math.min(1, Math.max(0, threshold)) });
    this.evaluateGate(this.deps.now());
  }

  /** One level sample (public for tests). */
  sampleMic(): number {
    if (!this.micAnalyser || !this.micBuf) {
      this.level = 0;
    } else {
      this.micAnalyser.getFloatTimeDomainData(this.micBuf);
      let sum = 0;
      for (let i = 0; i < this.micBuf.length; i++) sum += this.micBuf[i]! * this.micBuf[i]!;
      this.level = Math.min(1, Math.sqrt(sum / this.micBuf.length) * 4);
    }
    const now = this.deps.now();
    this.evaluateGate(now);
    this.sampleHowl(now);
    return this.level;
  }

  /** Uno spettro per il rilevatore di fischio (ogni 40 ms, insieme al livello). */
  private sampleHowl(now: number): void {
    const a = this.howlAnalyser;
    if (!a || !this.howlBuf || typeof a.getFloatFrequencyData !== 'function') return;
    a.getFloatFrequencyData(this.howlBuf);
    this.feedHowlSpectrum(this.howlBuf, now);
  }

  /**
   * Passa uno spettro (dB, come getFloatFrequencyData) al rilevatore. Pubblico per i test.
   * Gira solo mentre trasmetti: col microfono spento il giro casse -> microfono è già interrotto.
   */
  feedHowlSpectrum(magsDb: ArrayLike<number>, now: number, sampleRate = this.ctx?.sampleRate ?? 48_000, fftSize = HOWL_FFT_SIZE): void {
    if (!this.howlDetector) this.howlDetector = new HowlDetector(this.speakerMode ? HOWL_SPEAKERS : HOWL_HEADPHONES, sampleRate, fftSize);
    if (!this.snap.mic.transmitting) {
      this.howlDetector.reset();
      return;
    }
    const r = this.howlDetector.push(magsDb, now);
    if (r.detected) this.onHowl(r.frequency);
  }

  private onHowl(frequency: number | null): void {
    // Prima di tutto si smette di trasmettere: interrompe il giro casse -> microfono.
    this.setMuted(true);
    this.update({ howl: { detected: true, frequency } });
  }

  /** Feeds a level directly (tests). */
  feedMicLevel(level: number, now = this.deps.now()): void {
    this.level = level;
    this.evaluateGate(now);
  }

  private evaluateGate(now: number): void {
    this.lastGateNow = now;
    const mic = this.snap.mic;
    const thr = effectiveThreshold(mic.autoSensitivity, mic.threshold, this.speakerMode);
    let open = true;
    if (thr !== null) {
      if (this.level >= thr) this.gateHoldUntil = now + GATE_HOLD_MS;
      open = now < this.gateHoldUntil;
    }
    const speakLevel = mic.autoSensitivity ? SELF_SPEAKING_LEVEL : (thr ?? mic.threshold);
    if (this.level >= speakLevel) this.voiceHoldUntil = now + GATE_HOLD_MS;
    const speaking = mic.transmitting && open && this.level >= speakLevel;
    if (open !== mic.gateOpen) {
      this.updateMic({ gateOpen: open });
      this.applyGate();
    } else {
      this.refreshDucking(now);
    }
    if (speaking !== this.snap.selfSpeaking) this.update({ selfSpeaking: speaking });
  }

  private applyGate(): void {
    const enabled = this.snap.mic.transmitting && this.snap.mic.gateOpen;
    for (const r of [this.active, this.pending]) {
      if (r?.mic && this.snap.mic.transmitting) r.mic.mediaStreamTrack.enabled = enabled;
    }
  }

  getPeerAudio(): PeerAudioState[] {
    return this.mixer?.getPeerStates() ?? [];
  }

  dispose(): void {
    this.disposed = true;
    this.resetMicSync();
    this.releaseKeepAliveLock();
    this.cancelReconnect();
    void this.closeAll();
    this.stopMic();
    this.mixer?.dispose();
    this.output?.dispose();
    this.output = null;
    this.ns?.dispose();
    this.ns = null;
    void this.ctx?.close().catch(() => undefined);
    this.listeners.clear();
  }

  // ------------------------------------------------------------------ join / make-before-break

  private async join(a: RoomAssignMessage, isRetry: boolean): Promise<void> {
    // Same room already connected (e.g. central restart, §8.10): keep the connection, update epoch.
    if (this.active && this.active.name === a.room && !isRetry && this.isConnected(this.active.room)) {
      this.active.epoch = a.epoch;
      if (this.permissions?.epoch === a.epoch) this.applyPermissions(this.active);
      if (this.pending) this.closeRoom(this.pending);
      this.pending = null;
      this.send({ type: 'room.joined', epoch: a.epoch, room: a.room });
      this.update({ phase: 'connected', room: a.room });
      // epoch nuova: il centrale ha dimenticato `net` (es. WS ricollegato) e va rimandato
      this.startNetMonitor();
      return;
    }
    if (a.expiresAt - TOKEN_SAFETY_MS <= this.deps.now()) {
      this.requestToken();
      return;
    }
    if (this.pending) {
      this.closeRoom(this.pending);
      this.pending = null;
    }
    const ctx = this.ctx!;
    const room = this.deps.createRoom(ctx);
    const managed: ManagedRoom = { room, name: a.room, epoch: a.epoch, mic: null, publication: null, closing: false, tracks: new Map() };
    this.pending = managed;
    this.bindRoom(managed);
    this.update({ phase: this.active && !isRetry ? 'switching' : isRetry ? 'reconnecting' : 'connecting' });
    try {
      await room.connect(a.livekitUrl, a.token, { autoSubscribe: false });
      if (this.pending !== managed) throw new Superseded();
      // Who may hear me: set before publishing (deny-all until the central sends `permissions`).
      this.applyPermissions(managed);
      await this.publishMic(managed);
      if (this.pending !== managed) throw new Superseded();
      this.send({ type: 'room.joined', epoch: a.epoch, room: a.room });
      const old = this.active;
      this.active = managed;
      this.pending = null;
      this.reconnectTries = 0;
      if (old) this.closeRoom(old);
      this.attachExistingTracks(managed);
      this.update({ phase: 'connected', room: a.room, lastError: null });
      this.startNetMonitor();
    } catch (e) {
      if (e instanceof Superseded || this.pending !== managed) {
        this.closeRoom(managed);
        return;
      }
      this.pending = null;
      this.closeRoom(managed);
      const error = e instanceof Error ? e.message : String(e);
      this.send({ type: 'room.failed', epoch: a.epoch, room: a.room, error: error.slice(0, 500) });
      this.update({ phase: this.active ? 'connected' : 'failed', lastError: error });
    }
  }

  private isConnected(room: RoomLike): boolean {
    return room.state === ConnectionState.Connected || room.state === 'connected';
  }

  private requestToken(): void {
    this.awaitingToken = true;
    this.update({ phase: this.active ? this.snap.phase : 'connecting' });
    this.send({ type: 'token.request', epoch: this.epoch });
  }

  private bindRoom(m: ManagedRoom): void {
    const { room } = m;
    room.on(RoomEvent.TrackSubscribed, (track: RemoteTrack, pub: RemoteTrackPublication, participant: RemoteParticipant) => {
      if (m.closing) return;
      if (track.kind !== Track.Kind.Audio) return;
      // Attach only once the room is the active one, or while switching (make-before-break).
      this.attachTrack(m, track, pub, participant);
    });
    room.on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack, pub: RemoteTrackPublication) => {
      this.detachTrack(m, pub.trackSid ?? track.sid ?? '');
    });
    room.on(RoomEvent.ActiveSpeakersChanged, (speakers: Participant[]) => {
      if (m !== this.active) return;
      this.speaking = new Set(speakers.map((s) => s.identity));
      this.refreshPeers();
    });
    room.on(RoomEvent.Reconnecting, () => {
      if (m === this.active) this.update({ phase: 'reconnecting' });
    });
    room.on(RoomEvent.Reconnected, () => {
      if (m !== this.active) return;
      this.update({ phase: 'connected' });
      // dopo una riconnessione ICE il percorso può essere cambiato (es. da UDP a TURN)
      void this.probeNet();
    });
    room.on(RoomEvent.AudioPlaybackStatusChanged, () => {
      this.update({ audioBlocked: this.ctx?.state !== 'running' });
    });
    room.on(RoomEvent.Disconnected, (reason?: DisconnectReason) => {
      for (const sid of [...m.tracks.keys()]) this.detachTrack(m, sid);
      if (m.closing || m !== this.active) return;
      this.active = null;
      this.stopNetMonitor();
      this.onActiveLost(reason);
    });
  }

  private onActiveLost(reason?: DisconnectReason): void {
    this.speaking.clear();
    this.refreshPeers();
    const serverDriven =
      reason === DisconnectReason.PARTICIPANT_REMOVED ||
      reason === DisconnectReason.ROOM_DELETED ||
      reason === DisconnectReason.DUPLICATE_IDENTITY ||
      reason === DisconnectReason.CLIENT_INITIATED;
    if (!this.assignment || !this.snap.entered || this.pending) {
      this.update({ phase: this.snap.entered ? 'waiting' : 'idle', room: null });
      return;
    }
    if (serverDriven) {
      // The central decides what comes next (room.assign / room.leave).
      this.update({ phase: 'waiting', room: null });
      return;
    }
    // Network loss beyond the automatic reconnection of livekit-client: ask for a fresh token.
    if (this.reconnectTries >= MAX_RECONNECT_TRIES) {
      this.update({ phase: 'failed', room: null, lastError: 'Connessione vocale persa' });
      return;
    }
    const delay = Math.min(1000 * 2 ** this.reconnectTries, 8000);
    this.reconnectTries += 1;
    this.update({ phase: 'reconnecting', room: null });
    this.reconnectTimer = this.deps.setTimeout(() => {
      this.reconnectTimer = null;
      if (this.assignment && this.snap.entered && !this.active && !this.pending) this.requestToken();
    }, delay);
  }

  private cancelReconnect(): void {
    if (this.reconnectTimer !== null) this.deps.clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
  }

  private attachExistingTracks(m: ManagedRoom): void {
    for (const participant of m.room.remoteParticipants.values()) {
      for (const pub of participant.audioTrackPublications.values()) {
        // (publication.isSubscribed is always false with autoSubscribe=false: rely on the track.)
        if (pub.track && !m.tracks.has(pub.trackSid)) {
          this.attachTrack(m, pub.track as RemoteTrack, pub, participant);
        }
      }
    }
  }

  private attachTrack(m: ManagedRoom, track: RemoteTrack, pub: RemoteTrackPublication, participant: RemoteParticipant): void {
    if (!this.mixer) return;
    const sid = pub.trackSid ?? track.sid ?? participant.identity;
    if (m.tracks.has(sid)) return;
    const identity = participant.identity;
    const key = `${m.name}|${identity}|${sid}`;
    const nodes = this.mixer.addPeer(identity, key);
    const pref = this.opts.peerPref?.(identity);
    if (pref) {
      this.mixer.setPeerVolume(identity, pref.volume);
      this.mixer.setPeerMuted(identity, pref.muted);
    }
    const audioTrack = track as RemoteTrack & {
      setWebAudioPlugins?: (n: AudioNode[]) => void;
      attach?: () => HTMLMediaElement;
    };
    audioTrack.setWebAudioPlugins?.(nodes);
    // Chrome only feeds WebAudio from a remote stream attached to a media element; livekit keeps it muted.
    const element = audioTrack.attach?.() ?? null;
    m.tracks.set(sid, { identity, key, track, publication: pub, element });
    if (this.gating && !this.peerInfo.has(identity)) pub.setEnabled(false);
    this.refreshPeers();
  }

  private detachTrack(m: ManagedRoom, sid: string): void {
    const t = m.tracks.get(sid);
    if (!t) return;
    m.tracks.delete(sid);
    this.mixer?.removePeer(t.key);
    try {
      (t.track as RemoteTrack & { detach?: () => HTMLMediaElement[] }).detach?.();
    } catch {
      /* ignore */
    }
    t.element?.remove?.();
    this.refreshPeers();
  }

  private get gating(): boolean {
    return this.opts.gateUnassignedTracks !== false;
  }

  /** Publisher-side permissions: only the listeners chosen by the central may subscribe. */
  private applyPermissions(m: ManagedRoom): void {
    const allow = this.permissions && this.permissions.epoch === m.epoch ? this.permissions.allow : [];
    try {
      m.room.localParticipant.setTrackSubscriptionPermissions(
        false,
        allow.map((id) => ({ participantIdentity: id, allowAll: true })),
      );
    } catch {
      /* not connected anymore */
    }
  }

  /** Enable only tracks whose identity was assigned by the central. Never subscribes. */
  private applyGating(): void {
    if (!this.gating) return;
    for (const m of [this.active, this.pending]) {
      if (!m) continue;
      for (const t of m.tracks.values()) t.publication.setEnabled(this.peerInfo.has(t.identity));
    }
  }

  private closeRoom(m: ManagedRoom): void {
    if (m.closing) return;
    m.closing = true;
    for (const sid of [...m.tracks.keys()]) this.detachTrack(m, sid);
    m.mic?.stop();
    m.mic = null;
    void m.room.disconnect(true).catch(() => undefined);
  }

  private async closeAll(): Promise<void> {
    const rooms = [this.active, this.pending].filter((r): r is ManagedRoom => r !== null);
    this.active = null;
    this.pending = null;
    this.stopNetMonitor();
    for (const r of rooms) this.closeRoom(r);
  }

  // ------------------------------------------------------------------ microphone

  private async startMic(): Promise<void> {
    if (this.source) return;
    const m = await this.openMic();
    if (!m) return;
    this.source = m.raw;
    this.tx = m.tx;
    this.attachMicAnalyser();
  }

  private async acquireMic(): Promise<MediaStream | null> {
    if (!this.deps.isSecureContext()) {
      this.updateMic({ status: 'insecure' });
      return null;
    }
    this.updateMic({ status: 'starting' });
    const audio: MediaTrackConstraints = {
      echoCancellation: true,
      // Con RNNoise attivo la soppressione del browser si spegne: due soppressori in fila
      // "mangiano" la voce (suoni metallici, sillabe tagliate). L'AEC resta sempre acceso.
      noiseSuppression: !this.nsWanted(),
      // In modalità altoparlanti l'AGC resta spento: alzerebbe il guadagno proprio quando l'eco
      // delle casse arriva al microfono, favorendo il Larsen.
      autoGainControl: !this.speakerMode,
      channelCount: 1,
    };
    if (this.inputDeviceId) audio.deviceId = { exact: this.inputDeviceId };
    try {
      const stream = await this.deps.getUserMedia({ audio, video: false });
      this.updateMic({ status: 'ok' });
      return stream;
    } catch (e) {
      const name = e instanceof Error ? e.name : '';
      if (name === 'OverconstrainedError' && this.inputDeviceId) {
        // Saved device unplugged: fall back to the default one.
        this.inputDeviceId = null;
        return this.acquireMic();
      }
      const status: MicStatus =
        name === 'NotAllowedError' || name === 'SecurityError'
          ? 'denied'
          : name === 'NotFoundError'
            ? 'notfound'
            : name === 'NotReadableError' || name === 'AbortError'
              ? 'busy'
              : 'error';
      this.updateMic({ status });
      return null;
    }
  }

  /** Retry the microphone after a permission error (button in the UI). */
  async retryMic(): Promise<void> {
    if (this.source) return;
    await this.startMic();
    if (this.source) {
      for (const r of [this.active, this.pending]) if (r && !r.mic) await this.publishMic(r);
    }
  }

  private attachMicAnalyser(): void {
    if (!this.ctx || !this.source) return;
    this.micSourceNode?.disconnect();
    if (this.levelSourceNode !== this.micSourceNode) this.levelSourceNode?.disconnect();
    this.micSourceNode = this.ctx.createMediaStreamSource(this.source);
    // Livello/soglia sul segnale trasmesso (dopo RNNoise se attivo).
    this.levelSourceNode = this.tx && this.tx !== this.source ? this.ctx.createMediaStreamSource(this.tx) : this.micSourceNode;
    this.micAnalyser = this.ctx.createAnalyser();
    this.micAnalyser.fftSize = 1024;
    this.micBuf = new Float32Array(this.micAnalyser.fftSize);
    this.levelSourceNode.connect(this.micAnalyser);
    // Rilevatore di fischio: analyser dedicato con più risoluzione (≈23 Hz/bin a 48 kHz).
    this.howlAnalyser = this.ctx.createAnalyser();
    this.howlAnalyser.fftSize = HOWL_FFT_SIZE;
    this.howlAnalyser.smoothingTimeConstant = 0.5;
    this.howlBuf = new Float32Array(this.howlAnalyser.fftSize / 2);
    this.micSourceNode.connect(this.howlAnalyser);
    this.howlDetector = new HowlDetector(this.speakerMode ? HOWL_SPEAKERS : HOWL_HEADPHONES, this.ctx.sampleRate, HOWL_FFT_SIZE);
    if (!this.levelTimer && typeof setInterval === 'function') {
      // Nel worker: con la finestra ridotta a icona Chrome porterebbe un setInterval della pagina a
      // 1 Hz, e la soglia voce si aprirebbe/chiuderebbe in ritardo (voce a scatti per gli altri).
      this.levelTimer = startBackgroundTicker(LEVEL_TICK_MS, () => this.sampleMic());
    }
  }

  private stopMic(): void {
    this.levelTimer?.stop();
    this.levelTimer = null;
    this.level = 0;
    this.source?.getTracks().forEach((t) => t.stop());
    this.source = null;
    this.micSourceNode?.disconnect();
    if (this.levelSourceNode !== this.micSourceNode) this.levelSourceNode?.disconnect();
    this.micSourceNode = null;
    this.levelSourceNode = null;
    this.ns?.stop();
    this.tx = null;
    if (this.snap.noiseSuppression.status === 'on' || this.snap.noiseSuppression.status === 'loading') this.setNsStatus('off');
    this.micAnalyser = null;
    this.micBuf = null;
    this.howlAnalyser = null;
    this.howlBuf = null;
    this.howlDetector?.reset();
    if (this.snap.mic.status !== 'off' && this.snap.mic.status !== 'denied' && this.snap.mic.status !== 'insecure') {
      this.updateMic({ status: 'off' });
    }
    if (this.snap.selfSpeaking) this.update({ selfSpeaking: false });
  }

  private async publishMic(m: ManagedRoom): Promise<void> {
    if (!this.source) await this.startMic();
    const mst = (this.tx ?? this.source)?.getAudioTracks()[0];
    if (!mst || !this.ctx) return;
    const track = this.deps.createLocalTrack(mst.clone(), this.ctx);
    if (!this.snap.mic.transmitting) await track.mute();
    else track.mediaStreamTrack.enabled = this.snap.mic.gateOpen;
    m.mic = track;
    m.publication = await m.room.localParticipant.publishTrack(track as unknown as LocalAudioTrack, {
      source: Track.Source.Microphone,
      name: 'microphone',
      // Opus mono 48 kbit/s (preset "music"): voce piena; lo stereo e' inutile, il 3D lo fa chi ascolta
      audioPreset: AudioPresets.music,
      dtx: true,
      red: true,
    });
  }

  private computeTransmit(mic: VoiceSnapshot['mic']): boolean {
    if (mic.status !== 'ok' && mic.status !== 'starting') return false;
    if (mic.muted || mic.deafened) return false;
    return mic.mode === 'vad' || mic.pttPressed;
  }

  private async applyTransmit(): Promise<void> {
    const on = this.snap.mic.transmitting;
    for (const r of [this.active, this.pending]) {
      if (!r?.mic) continue;
      try {
        if (on) {
          await r.mic.unmute();
          r.mic.mediaStreamTrack.enabled = this.snap.mic.gateOpen;
        } else await r.mic.mute();
      } catch {
        /* track ended */
      }
    }
  }

  /**
   * Dispositivo di uscita. Con il percorso anti-eco le voci escono dall'elemento <audio>, quindi
   * si usa `audioEl.setSinkId()`; se il browser non lo supporta si resta sull'uscita di sistema
   * (false). Anche gli elementi muted di livekit seguono il dispositivo (switchActiveDevice): su
   * Chrome l'AEC con uscite non predefinite funziona meglio così (chromium issue 40252911).
   */
  private async applyOutputDevice(deviceId: string): Promise<boolean> {
    if (!this.ctx) return true;
    let ok: boolean;
    if (this.output?.usesElement) {
      ok = await this.output.setSinkId(deviceId);
    } else {
      // Ripiego (nessun elemento <audio>): uscita dell'AudioContext, come prima.
      const ctx = this.ctx as AudioContext & { setSinkId?: (id: string) => Promise<void> };
      if (typeof ctx.setSinkId !== 'function') return false;
      try {
        await ctx.setSinkId(deviceId);
        ok = true;
      } catch {
        ok = false;
      }
    }
    if (ok) {
      for (const r of [this.active, this.pending]) {
        try {
          await r?.room.switchActiveDevice?.('audiooutput', deviceId).catch(() => false);
        } catch {
          /* non supportato: le voci escono comunque dall'elemento <audio> */
        }
      }
    }
    return ok;
  }

  // ------------------------------------------------------------------ network path (net)

  /** Room attiva connessa (o di nuovo connessa): misura subito e avvia il timer periodico. */
  private startNetMonitor(): void {
    if (this.netTimer === null) {
      try {
        this.netTimer = this.deps.setInterval(() => this.onNetTick(), NET_TICK_MS);
      } catch {
        this.netTimer = null;
      }
    }
    void this.probeNet();
  }

  private stopNetMonitor(): void {
    if (this.netTimer !== null) this.deps.clearInterval(this.netTimer);
    this.netTimer = null;
    if (this.snap.selfNet) this.update({ selfNet: null });
  }

  /** Ogni 2 s: misura se per l'epoch attiva non è ancora stata inviata, altrimenti ogni 10 s. */
  private onNetTick(): void {
    const m = this.active;
    if (!m) return;
    const sentForEpoch = this.lastNet !== null && this.lastNet.epoch === m.epoch;
    if (!sentForEpoch || this.deps.now() - this.netProbedAt >= NET_PROBE_MS) void this.probeNet();
  }

  /**
   * Legge le statistiche WebRTC della Room attiva (vedi netStats.ts) e invia `net` al centrale solo se il
   * risultato cambia o se per questa epoch non è ancora stato inviato. Statistiche assenti: non manda nulla.
   * Pubblico per i test.
   * @returns il percorso misurato, o null
   */
  async probeNet(): Promise<NetPath | null> {
    const m = this.active;
    if (!m || m.closing || this.disposed || !this.isConnected(m.room) || this.netBusy) return null;
    this.netBusy = true;
    let path: NetPath | null = null;
    try {
      this.netProbedAt = this.deps.now();
      let engine: EngineLike | undefined;
      try {
        engine = m.room.engine;
      } catch {
        engine = undefined;
      }
      path = await collectNetPath(netStatsSources(m.mic, engine));
    } catch {
      path = null;
    } finally {
      this.netBusy = false;
    }
    if (!path || m !== this.active || m.closing) return null;
    const cur = this.snap.selfNet;
    if (!cur || cur.net !== path.net || cur.via !== path.via) this.update({ selfNet: path });
    // epoch di `m` (aggiornata anche dal percorso "stessa stanza"); il centrale ignora quelle non correnti
    const epoch = m.epoch;
    if (epoch !== this.epoch) return path;
    const last = this.lastNet;
    if (!last || last.epoch !== epoch || last.net !== path.net || last.via !== path.via) {
      if (this.send({ type: 'net', epoch, net: path.net, via: path.via })) this.lastNet = { epoch, ...path };
    }
    return path;
  }

  // ------------------------------------------------------------------ peers view

  private refreshPeers(): void {
    const ids = new Set<string>();
    for (const m of [this.active, this.pending]) if (m) for (const t of m.tracks.values()) ids.add(t.identity);
    for (const id of this.peerInfo.keys()) ids.add(id);
    const peers: PeerView[] = [...ids].map((identity) => {
      const info = this.peerInfo.get(identity);
      return {
        identity,
        name: info?.name ?? this.participantName(identity) ?? identity.slice(0, 8),
        teamId: info?.teamId ?? null,
        role: info?.role ?? 'PLAYER',
        radio: info?.radio ?? false,
        ...(info?.net && info.via ? { net: info.net, via: info.via } : {}),
        speaking: this.speaking.has(identity),
      };
    });
    peers.sort((a, b) => a.name.localeCompare(b.name, 'it'));
    this.update({ peers });
  }

  private participantName(identity: string): string | null {
    const p = this.active?.room.remoteParticipants.get(identity);
    return p?.name || null;
  }

  // ------------------------------------------------------------------ E2E hook (§11.2)

  /** Test helpers exposed on window.__voiceTest when enabled. */
  testHook() {
    return {
      snapshot: () => this.snap,
      epoch: () => this.epoch,
      room: () => this.active?.name ?? null,
      publications: () => {
        // NOTE: with autoSubscribe=false livekit-client reports publication.isSubscribed === false
        // even when the server subscribed us; `isSubscribed` here means "a track was received".
        const out: Array<{ identity: string; trackSid: string; isSubscribed: boolean; isEnabled: boolean; attached: boolean }> = [];
        const m = this.active;
        if (!m) return out;
        for (const p of m.room.remoteParticipants.values()) {
          for (const pub of p.audioTrackPublications.values()) {
            const track = pub.track as (RemoteTrack & { mediaStreamTrack?: MediaStreamTrack }) | undefined;
            out.push({
              identity: p.identity,
              trackSid: pub.trackSid,
              isSubscribed: !!track && track.mediaStreamTrack?.readyState !== 'ended',
              isEnabled: pub.isEnabled,
              attached: m.tracks.has(pub.trackSid),
            });
          }
        }
        return out;
      },
      peerAudio: () => this.getPeerAudio(),
      enableMonitor: (fftSize?: number) => this.mixer?.enableMonitor(fftSize) ?? null,
      sampleRate: () => this.ctx?.sampleRate ?? null,
      /** Ultimo tipo di collegamento misurato (`direct`/`relay`), null se sconosciuto. */
      net: () => this.snap.selfNet,
    };
  }
}

function clamp01(v: number): number {
  return Math.min(1, Math.max(0, Number.isFinite(v) ? v : 0));
}

/** Soglia effettiva del gate; null = sempre aperto (sensibilità automatica in cuffia). */
export function effectiveThreshold(autoSensitivity: boolean, threshold: number, speakerMode: boolean): number | null {
  if (!autoSensitivity) return speakerMode ? Math.max(threshold, SPEAKER_MIN_THRESHOLD) : threshold;
  return speakerMode ? SPEAKER_AUTO_GATE : null;
}

class Superseded extends Error {
  constructor() {
    super('superseded');
  }
}
